/**
 * FDRSubmissionsViewer — plan-admin view of universe files submitted by FDRs.
 *
 * Filters by protocol / universe / period. Each row exposes the validation
 * summary captured at submission time and a download link to the decrypted
 * file (download is audit-logged server-side).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { SCRUBBER_API_BASE } from '../../api/config';

const PROTOCOLS = ['ODAG', 'CDAG', 'FA', 'SNPCC', 'CPE'];

export default function FDRSubmissionsViewer() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [protocol, setProtocol] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (protocol)    params.set('protocol', protocol);
      if (periodStart) params.set('period_start', periodStart);
      if (periodEnd)   params.set('period_end',   periodEnd);
      const res = await fetch(`${SCRUBBER_API_BASE}/api/fdr/submissions?${params.toString()}`, {
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Could not load submissions.');
        return;
      }
      setRows(json.submissions || []);
    } catch (e) {
      setError(e.message || 'Could not load submissions.');
    } finally {
      setLoading(false);
    }
  }, [protocol, periodStart, periodEnd]);

  useEffect(() => { load(); }, [load]);

  function download(id) {
    const url = `${SCRUBBER_API_BASE}/api/fdr/submissions/${encodeURIComponent(id)}/download`;
    window.location.href = url;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">FDR Submission Portal</div>
        <h1 className="text-2xl font-bold mt-1">Received submissions</h1>
        <p className="text-sm text-slate-500 mt-1">Universe files received from FDRs. Downloads are audit-logged.</p>
      </header>

      <div className="flex flex-wrap gap-2 mb-4 items-end">
        <Field label="Protocol">
          <select value={protocol} onChange={(e) => setProtocol(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
            <option value="">All</option>
            {PROTOCOLS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Period start">
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
        </Field>
        <Field label="Period end">
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
        </Field>
        <button onClick={load} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50 h-[34px]">Refresh</button>
      </div>

      {error && <div className="mb-3 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading && <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">No submissions yet.</div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">FDR</th>
                <th className="text-left px-4 py-2 font-semibold">Protocol / Universe</th>
                <th className="text-left px-4 py-2 font-semibold">Period</th>
                <th className="text-left px-4 py-2 font-semibold">File</th>
                <th className="text-left px-4 py-2 font-semibold">Validation</th>
                <th className="text-left px-4 py-2 font-semibold">Submitted</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const v = r.validation_summary || {};
                const ok = v.passed;
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{r.fdr_organization_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.fdr_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.protocol}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.universe_type}</div>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">{r.universe_period_start} → {r.universe_period_end}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs truncate max-w-xs">{r.file_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{(r.file_size / 1024).toFixed(1)} KB · {r.encrypted ? 'encrypted' : 'plaintext'}</div>
                    </td>
                    <td className="px-4 py-3">
                      {ok ? <span className="text-emerald-600 text-xs font-semibold">Passed</span>
                          : <span className="text-amber-600 text-xs font-semibold">{(v.row_errors || 0)} row · {(v.header_issues || 0)} header</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.submitted_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => download(r.id)} className="text-rose-600 hover:text-rose-700 text-xs font-semibold">
                        Download
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
